/**
 * EventSurveysPanel — organizer-side aggregate view of post-event
 * survey responses. Rendered as a tab inside the EventDashboard.
 *
 * Stats card: response rate, avg rating, NPS.
 * Comment list: with one-click "Feature as testimonial" toggle.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Star, Loader2, MessageSquare, Send, Award, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventSurveysPanel({ eventId }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState({ surveys: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/events/${eventId}/surveys`, { headers });
      setData(res.data || { surveys: [], stats: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load surveys');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  useEffect(() => { load(); }, [load]);

  const dispatchNow = async () => {
    if (!window.confirm('Send post-event surveys to all completed ticket buyers now?')) return;
    setDispatching(true);
    try {
      const res = await axios.post(`${API}/admin/surveys/dispatch?event_id=${eventId}`, {}, { headers });
      toast.success(`Surveys dispatched · ${res.data?.sent || 0} sent`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dispatch failed (admin only)');
    } finally {
      setDispatching(false);
    }
  };

  const toggleTestimonial = async (s) => {
    try {
      await axios.post(
        `${API}/events/${eventId}/surveys/${s.survey_id}/feature`,
        { is_testimonial: !s.is_testimonial },
        { headers },
      );
      toast.success(s.is_testimonial ? 'Removed from testimonials' : 'Featured as testimonial');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  const { stats, surveys } = data;
  const completed = surveys.filter((s) => s.status === 'completed');

  return (
    <div className="space-y-5" data-testid="event-surveys-panel">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Response rate</div>
            <div className="text-2xl font-bold text-foreground mt-1" data-testid="stat-response-rate">
              {stats.response_rate_pct ?? 0}%
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {stats.responses ?? 0} of {stats.total_sent ?? 0} sent
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Avg rating</div>
            <div className="text-2xl font-bold text-amber-300 mt-1 flex items-center gap-1.5" data-testid="stat-avg-rating">
              {stats.avg_rating ?? '—'}
              <Star className="h-4 w-4 fill-amber-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">NPS</div>
            <div className={`text-2xl font-bold mt-1 ${stats.nps >= 50 ? 'text-emerald-300' : stats.nps >= 0 ? 'text-amber-300' : 'text-rose-300'}`} data-testid="stat-nps">
              {stats.nps == null ? '—' : stats.nps}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {stats.promoters || 0}P · {stats.passives || 0}M · {stats.detractors || 0}D
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Testimonials</div>
            <div className="text-2xl font-bold text-foreground mt-1" data-testid="stat-testimonials">
              {completed.filter((s) => s.is_testimonial).length}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={dispatchNow}
          disabled={dispatching}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="dispatch-surveys-btn"
        >
          {dispatching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          Send surveys now
        </Button>
      </div>

      {/* Responses */}
      {completed.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-10 text-center">
            <BarChart3 className="h-7 w-7 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-semibold text-foreground">No responses yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Surveys auto-send 24h after the event ends. Or click "Send surveys now" above to dispatch immediately.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="survey-responses-list">
          {completed.map((s) => (
            <Card key={s.survey_id} className="bg-card border-border" data-testid={`survey-row-${s.survey_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold text-foreground">{s.buyer_name || s.buyer_email}</span>
                      <div className="flex items-center gap-0.5">
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} className={`h-3.5 w-3.5 ${
                            n <= (s.ratings?.overall || 0) ? 'fill-amber-300 text-amber-300' : 'text-muted-foreground/40'
                          }`} />
                        ))}
                      </div>
                      {s.nps !== null && s.nps !== undefined && (
                        <Badge variant="outline" className={`text-[10px] ${
                          s.nps >= 9 ? 'text-emerald-300 border-emerald-700/40' :
                          s.nps >= 7 ? 'text-amber-300 border-amber-700/40' :
                          'text-rose-300 border-rose-700/40'
                        }`}>NPS {s.nps}</Badge>
                      )}
                      {s.would_attend_again === true && (
                        <Badge variant="outline" className="text-[10px] text-emerald-300 border-emerald-700/40">Would return</Badge>
                      )}
                      {s.is_testimonial && (
                        <Badge variant="outline" className="text-[10px] text-amber-300 border-amber-700/40">
                          <Award className="h-2.5 w-2.5 mr-1" /> Testimonial
                        </Badge>
                      )}
                    </div>
                    {s.comments && (
                      <p className="text-sm text-foreground/80 mt-2 leading-snug">
                        <MessageSquare className="h-3 w-3 inline mr-1 text-muted-foreground" />
                        &ldquo;{s.comments}&rdquo;
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-2">
                      {s.submitted_at && new Date(s.submitted_at).toLocaleString()}
                    </p>
                  </div>
                  {s.comments && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleTestimonial(s)}
                      data-testid={`feature-toggle-${s.survey_id}`}
                      className={s.is_testimonial ? 'text-amber-300' : ''}
                    >
                      <Award className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
